import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@/components/Card';
import { CardHeader } from '@/components/CardHeader';
import { KPICard } from '@/components/KPICard';
import { StatusDot } from '@/components/StatusDot';
import { Sparkline } from '@/components/Sparkline';
import { RatioBar } from '@/components/RatioBar';
import { BarList } from '@/components/BarList';
import { SevBadge } from '@/components/SevBadge';
import { EmptyState } from '@/components/EmptyState';
import { Activity, AlertCircle, Check, LayoutGrid } from 'lucide-react';
import { formatNumber, formatDate, formatPercent, formatRelativeTime } from '@/lib/locale';
import { apiGet, lastNDays, qs } from '@/features/_shared/queries';
import { QueryState } from '@/features/_shared/QueryState';

// ----- API response shapes (tolerant; backend OpenAPI is authoritative) -----

type AnalyticsOverview = {
  total_messages: number;
  pass_rate: number;
  spf_pass_rate?: number;
  dkim_pass_rate?: number;
  reports_today?: number;
  delta_messages_pct?: number;
  delta_pass_pp?: number;
  open_alerts?: { critical: number; warning: number; info: number };
};

type Tenant = {
  id: string;
  slug: string;
  name: string;
  status?: 'good' | 'warn' | 'fail' | 'onboarding';
  domains_count?: number;
  volume_7d?: number;
  pass_7d?: number;
  alerts_count?: number;
  monitored_since?: string;
  last_report_at?: string;
};

type AlertEvent = {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  summary: string;
  tenant_slug: string;
  tenant_name: string;
  triggered_at: string;
  acknowledged_at?: string | null;
};

type TimelinePoint = {
  ts: string; // ISO date (backend TimelineBucket.ts)
  pass: number;
  quarantine: number;
  reject: number;
};

// Mirrors the backend ``IngestHealthOut`` (GET /system/ingest-health). The
// earlier shape invented fields (messages_per_minute, opensearch_shards,
// last_backup_at, status) the endpoint never sends, so they came back
// ``undefined`` and ``formatNumber(undefined)`` rendered a literal "NaN".
type IngestHealth = {
  messages_last_hour?: number | null;
  parse_success_rate?: number | null; // 0..1
  unmatched_count?: number | null;
  queue_depth?: number | null;
  recent_unmatched?: unknown[];
};

// The backend doesn't emit a status verdict, so derive one from the real
// signals: a deep maildir backlog or unmatched files mean the pipeline needs
// attention. Undefined health (request still loading / failed) → "warn".
function ingestStatus(h?: IngestHealth): 'good' | 'warn' | 'fail' {
  if (!h) return 'warn';
  const queue = h.queue_depth ?? 0;
  const unmatched = h.unmatched_count ?? 0;
  if (queue > 500 || unmatched > 50) return 'fail';
  if (queue > 100 || unmatched > 0) return 'warn';
  return 'good';
}

// ----- Component -----------------------------------------------------------

export function GlobalDashboard() {
  const range = useMemo(() => lastNDays(30), []);

  const overviewQ = useQuery({
    queryKey: ['analytics', 'overview', 'all'],
    queryFn: () =>
      apiGet<AnalyticsOverview>(`/analytics/overview${qs({ tenant: 'all' })}`, {
        tenant: 'all',
      }),
  });

  const tenantsQ = useQuery({
    queryKey: ['tenants'],
    queryFn: () => apiGet<Tenant[]>('/tenants', { tenant: 'all' }),
  });

  const alertsQ = useQuery({
    queryKey: ['alerts', 'recent'],
    queryFn: () =>
      apiGet<AlertEvent[]>(`/alert-events${qs({ tenant: 'all', page: 1, per_page: 10 })}`, {
        tenant: 'all',
      }),
  });

  const timelineQ = useQuery({
    queryKey: ['analytics', 'timeline', 'all', range.from, range.to],
    queryFn: () =>
      apiGet<{ points: TimelinePoint[] }>(
        `/analytics/timeline${qs({
          tenant: 'all',
          granularity: 'day',
          since: range.from,
          until: range.to,
        })}`,
        { tenant: 'all' },
      ),
  });

  const ingestQ = useQuery({
    queryKey: ['system', 'ingest-health'],
    queryFn: () => apiGet<IngestHealth>('/system/ingest-health', { tenant: 'all' }),
  });

  const overview = overviewQ.data;
  const tenants = tenantsQ.data ?? [];
  const alerts = alertsQ.data ?? [];
  const timeline = timelineQ.data?.points ?? [];

  const totalDomains = tenants.reduce((s, t) => s + (t.domains_count ?? 0), 0);
  const openAlerts = alerts.filter((a) => !a.acknowledged_at).length;
  const openBreakdown = overview?.open_alerts;

  const isLoading = overviewQ.isLoading || tenantsQ.isLoading || timelineQ.isLoading;
  const isError = overviewQ.isError || tenantsQ.isError;

  const onRetry = () => {
    overviewQ.refetch();
    tenantsQ.refetch();
    alertsQ.refetch();
    timelineQ.refetch();
    ingestQ.refetch();
  };

  return (
    <div className="max-w-[1600px] space-y-5 p-5">
      {/* Header strip */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-slate-900">RuaHQ · Globaal</h1>
          <p className="mt-0.5 text-[13px] text-slate-500">
            {tenants.length} tenants · {formatNumber(totalDomains)} domeinen ·
            {ingestStatus(ingestQ.data) === 'good'
              ? ' ingest gezond'
              : ingestStatus(ingestQ.data) === 'warn'
                ? ' ingest — aandacht'
                : ' ingest — storing'}
          </p>
        </div>
        <div className="font-mono text-[11px] text-slate-500">
          Laatste sync · {formatDate(new Date())}
        </div>
      </div>

      <QueryState isLoading={isLoading} isError={isError} onRetry={onRetry}>
        {/* KPI row */}
        <div className="grid grid-cols-4 gap-3">
          <KPICard
            label="Gemonitorde domeinen"
            value={formatNumber(totalDomains)}
            sub={`${tenants.length} tenants`}
          />
          <KPICard
            label="Aggregate reports vandaag"
            value={formatNumber(overview?.reports_today ?? 0)}
            delta={
              overview?.delta_messages_pct != null
                ? `${overview.delta_messages_pct >= 0 ? '+' : ''}${overview.delta_messages_pct.toFixed(1).replace('.', ',')}% vs gisteren`
                : undefined
            }
            deltaTone={
              overview?.delta_messages_pct == null
                ? 'flat'
                : overview.delta_messages_pct >= 0
                  ? 'up'
                  : 'down'
            }
            sub={`${formatNumber(overview?.total_messages ?? 0)} berichten · 30d`}
          />
          <KPICard
            label="Open kritieke alerts"
            value={String(openAlerts)}
            accent={openAlerts > 0 ? 'text-red-700' : undefined}
            sub={
              openBreakdown
                ? `${openBreakdown.critical} critical · ${openBreakdown.warning} warning · ${openBreakdown.info} info`
                : '—'
            }
            delta={openAlerts > 0 ? `${openAlerts} ack vereist` : undefined}
            deltaTone="warn"
          />
          <PipelineStatusCard health={ingestQ.data} />
        </div>

        {/* Tenant table + recent alerts */}
        <div className="grid grid-cols-12 gap-5">
          <Card className="col-span-8">
            <CardHeader
              title="Tenant overzicht"
              subtitle="klik op een tenant voor detail"
              right={
                <Link to="/tenants" className="text-[11px] text-info-700 hover:text-info-600">
                  Alles →
                </Link>
              }
            />
            {tenants.length === 0 ? (
              <EmptyState
                title="Nog geen tenants geconfigureerd"
                body="Voeg een eerste tenant toe om DMARC-rapportages te ontvangen. SerSo voorziet de DNS-aliasrecords binnen één klik."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <Th>Tenant</Th>
                      <Th align="right">#&nbsp;Dom.</Th>
                      <Th>7d pass</Th>
                      <Th>7d trend</Th>
                      <Th align="right">Volume 7d</Th>
                      <Th>Laatste rapport</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {tenants.map((t) => {
                      const spark = synthSparkline(t.id, t.volume_7d ?? 100);
                      const status = t.status ?? 'good';
                      return (
                        <tr key={t.id} className="group cursor-pointer hover:bg-slate-50">
                          <td className="px-3 py-2.5">
                            <Link to="/tenants/$slug" params={{ slug: t.slug }} className="block">
                              <div className="flex items-center gap-2">
                                <StatusDot status={status} />
                                <span className="font-medium text-slate-900 group-hover:text-brand-700">
                                  {t.name}
                                </span>
                              </div>
                              {t.monitored_since && (
                                <span className="ml-4 mt-0.5 block font-mono text-[10px] text-slate-400">
                                  sinds {formatDate(t.monitored_since)}
                                </span>
                              )}
                            </Link>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-slate-700">
                            {t.domains_count ?? 0}
                          </td>
                          <td className="px-3 py-2.5">
                            <RatioBar pct={t.pass_7d ?? null} />
                          </td>
                          <td className="px-3 py-2.5">
                            <Sparkline
                              data={spark}
                              color={
                                status === 'fail'
                                  ? '#dc2626'
                                  : status === 'warn'
                                    ? '#d97706'
                                    : '#16a34a'
                              }
                            />
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono tabular-nums text-slate-700">
                            {formatNumber(t.volume_7d ?? 0)}
                          </td>
                          <td className="px-3 py-2.5 text-slate-600">
                            {t.last_report_at ? formatRelativeTime(t.last_report_at) : '—'}
                          </td>
                          <td className="px-3 py-2.5">
                            {(t.alerts_count ?? 0) > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-red-700">
                                <AlertCircle size={10} />
                                {t.alerts_count}
                              </span>
                            ) : status === 'onboarding' ? (
                              <span className="font-mono text-[10px] text-slate-400">
                                onboarding
                              </span>
                            ) : (
                              <Check size={13} className="text-green-600" />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Recent alerts */}
          <Card className="col-span-4 flex flex-col">
            <CardHeader
              title="Recente alerts"
              subtitle={`${alerts.length} meest recent · ${openAlerts} open`}
            />
            {alerts.length === 0 ? (
              <EmptyState
                title="Nog geen alerts"
                body="Er zijn geen actieve waarschuwingen. Alerts verschijnen automatisch bij spoofing, beleid-dalingen of onbekende verzendbronnen."
                compact
              />
            ) : (
              <div className="max-h-[520px] flex-1 divide-y divide-slate-100 overflow-auto">
                {alerts.map((a) => (
                  <Link
                    key={a.id}
                    to="/tenants/$slug/alerts"
                    params={{ slug: a.tenant_slug }}
                    className={`block px-4 py-3 transition-colors hover:bg-slate-50 ${
                      a.acknowledged_at ? 'opacity-70' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <SevBadge sev={a.severity} />
                      <span className="mt-0.5 whitespace-nowrap font-mono text-[10px] text-slate-400">
                        {formatRelativeTime(a.triggered_at)}
                      </span>
                    </div>
                    <div className="mt-1.5 text-[12.5px] leading-snug text-slate-900">
                      {a.summary}
                    </div>
                    <div className="mt-1 flex items-center justify-between text-[10px] text-slate-500">
                      <span className="font-medium">{a.tenant_name}</span>
                      {a.acknowledged_at && (
                        <span className="inline-flex items-center gap-1 text-green-700">
                          <Check size={10} />
                          ack
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* 30d trend + top reporters */}
        <div className="grid grid-cols-12 gap-5">
          <Card className="col-span-8">
            <CardHeader
              title="30 dagen trend · alle tenants"
              subtitle="dagvolume gestapeld per uitkomst"
            />
            <div className="p-4">
              <TimelineChart points={timeline} />
            </div>
          </Card>

          <Card className="col-span-4">
            <CardHeader title="Top 10 reporters" subtitle="30d aggregate XML-rapporten ontvangen" />
            <div className="p-4">
              <TopReporters />
            </div>
          </Card>
        </div>

        {/* System health */}
        <Card>
          <CardHeader
            title="Systeem-gezondheid"
            subtitle="RuaHQ platform infrastructuur"
            right={
              <span
                className={`inline-flex items-center gap-1.5 text-[11px] ${
                  ingestStatus(ingestQ.data) === 'good' ? 'text-green-700' : 'text-amber-700'
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    ingestStatus(ingestQ.data) === 'good'
                      ? 'animate-pulse bg-green-600'
                      : 'bg-amber-600'
                  }`}
                />
                {ingestStatus(ingestQ.data) === 'good' ? 'Operationeel' : 'Aandacht'}
              </span>
            }
          />
          <SystemHealthStrip health={ingestQ.data} />
        </Card>
      </QueryState>
    </div>
  );
}

// ----- Sub-components ------------------------------------------------------

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      className={`px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 text-${align}`}
    >
      {children}
    </th>
  );
}

function PipelineStatusCard({ health }: { health?: IngestHealth }) {
  const status = ingestStatus(health);
  const tone =
    status === 'good' ? 'text-green-700' : status === 'warn' ? 'text-amber-700' : 'text-red-700';
  const dot =
    status === 'good'
      ? 'bg-green-600 animate-pulse'
      : status === 'warn'
        ? 'bg-amber-600'
        : 'bg-red-600';
  const label = status === 'good' ? 'Gezond' : status === 'warn' ? 'Aandacht' : 'Storing';
  return (
    <Card className="px-4 py-3">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        Ingest pipeline
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <div className={`text-2xl font-bold ${tone} inline-flex items-center gap-2 tracking-tight`}>
          <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
          {label}
        </div>
      </div>
      <div className="mt-0.5 text-[11px] text-slate-500">
        {health
          ? `${
              health.messages_last_hour != null
                ? `${formatNumber(health.messages_last_hour)} berichten/u`
                : 'geen recente metriek'
            } · wachtrij ${health.queue_depth != null ? formatNumber(health.queue_depth) : '—'}`
          : 'Status onbekend'}
      </div>
    </Card>
  );
}

function SystemHealthStrip({ health }: { health?: IngestHealth }) {
  type Cell = {
    Icon: typeof Activity;
    label: string;
    value: string;
    unit: string;
    trend: string;
    status: 'good' | 'warn' | 'fail';
  };
  const cells: Cell[] = [
    {
      Icon: Activity,
      label: 'Ingest · laatste uur',
      value: health?.messages_last_hour != null ? formatNumber(health.messages_last_hour) : '—',
      unit: 'berichten',
      trend: health?.messages_last_hour != null ? 'live' : 'metriek volgt',
      status: ingestStatus(health),
    },
    {
      Icon: Check,
      label: 'Parse-succes',
      value: health?.parse_success_rate != null ? formatPercent(health.parse_success_rate) : '—',
      unit: '',
      trend: health?.parse_success_rate != null ? 'laatste uur' : 'metriek volgt',
      status:
        health?.parse_success_rate == null
          ? 'good'
          : health.parse_success_rate >= 0.95
            ? 'good'
            : 'warn',
    },
    {
      Icon: LayoutGrid,
      label: 'Wachtrij · maildir',
      value: health?.queue_depth != null ? formatNumber(health.queue_depth) : '—',
      unit: 'in wacht',
      trend: (health?.queue_depth ?? 0) === 0 ? 'leeg' : 'verwerking',
      status: (health?.queue_depth ?? 0) < 100 ? 'good' : 'warn',
    },
    {
      Icon: AlertCircle,
      label: 'Unmatched',
      value: health?.unmatched_count != null ? formatNumber(health.unmatched_count) : '—',
      unit: 'bestanden',
      trend: (health?.unmatched_count ?? 0) === 0 ? 'geen' : 'nazicht nodig',
      status: (health?.unmatched_count ?? 0) === 0 ? 'good' : 'warn',
    },
  ];

  return (
    <div className="grid grid-cols-4 divide-x divide-slate-200">
      {cells.map((cell) => (
        <div key={cell.label} className="px-4 py-3">
          <div className="mb-1 flex items-center gap-2">
            <cell.Icon size={13} className="text-slate-400" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {cell.label}
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-[18px] font-bold tabular-nums text-slate-900">{cell.value}</span>
            <span className="text-[11px] text-slate-500">{cell.unit}</span>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[11px] text-slate-500">{cell.trend}</span>
            <StatusDot status={cell.status} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineChart({ points }: { points: TimelinePoint[] }) {
  if (points.length === 0) {
    return (
      <div className="flex h-[220px] items-center justify-center text-[12px] text-slate-500">
        Nog geen data voor deze periode.
      </div>
    );
  }
  const data = points.map((p) => ({
    day: p.ts ? formatDate(p.ts, 'd MMM') : '',
    pass: p.pass,
    quarantine: p.quarantine,
    reject: p.reject,
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={data} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
        <CartesianGrid stroke="#e2e8f0" strokeDasharray="0" vertical={false} />
        <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#64748b' }} />
        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} />
        <Tooltip
          formatter={(v: number) => formatNumber(v)}
          labelStyle={{ fontSize: 11 }}
          contentStyle={{ fontSize: 11, borderRadius: 4 }}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="pass" stackId="x" fill="#16a34a" name="Pass" />
        <Bar dataKey="quarantine" stackId="x" fill="#d97706" name="Quarantine" />
        <Bar dataKey="reject" stackId="x" fill="#dc2626" name="Reject" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function TopReporters() {
  const q = useQuery({
    queryKey: ['analytics', 'reporters', 'all'],
    queryFn: () =>
      apiGet<Array<{ name: string; count: number }>>(
        `/analytics/reporters${qs({ tenant: 'all', size: 10 })}`,
        { tenant: 'all' },
      ),
  });
  if (q.isLoading) {
    return <div className="text-[12px] text-slate-500">Laden…</div>;
  }
  if (!q.data || q.data.length === 0) {
    return <div className="py-4 text-[12px] text-slate-500">Geen reporters in deze periode.</div>;
  }
  return <BarList items={q.data.map((r) => ({ label: r.name, value: r.count }))} />;
}

// Deterministic, idempotent sparkline placeholder from an id seed.
// Replaced once the backend exposes per-tenant 7-day series.
function synthSparkline(seed: string, base: number): number[] {
  let s = 0;
  for (const c of seed) s = (s + c.charCodeAt(0)) % 997;
  return Array.from({ length: 14 }, (_, i) => {
    const wobble = ((s + i * 31) % 17) / 17 - 0.5;
    return Math.max(1, Math.round(base * (1 + wobble * 0.4)));
  });
}
